import { Customer, CustomerVideoFeedback, PaginatedAdminNewUserQuizSubmissions } from '@/shared/types/Admin';

import { BASE_URL } from '../constants';
import { del, get, patch, post, put } from '../requestMethods';
import { ONSCREEN_ADMIN_API } from './onscreen';
import { RECORDING_ADMIN_API } from './recordings';
import {
  CreateCustomerDeductionRequestData,
  CreateCustomerGrantRequestData,
  CustomerFeatures,
  GetCSVRequestData,
  GetCustomersFeedbackRequestData,
  GetCustomersRequestData,
  GetNewUserQuizSubmissionsRequestData,
  GrantCustomerEnterpriseRequestData,
  GrantCustomerFeatureRequestData,
  RevokeCustomerFeatureRequestData,
  UpdateCustomerRequestData,
} from './types';

const URL = `${BASE_URL}/api/admin`;
export const ADMIN_API = {
  grantCustomerEnterprise: ({ customerId, ...rest }: GrantCustomerEnterpriseRequestData) =>
    post<Customer>(`${URL}/users/${customerId}/subscriptions/enterprise`, { data: rest }),
  grantCustomerFeature: ({ customerId, feature, ...rest }: GrantCustomerFeatureRequestData) =>
    put<CustomerFeatures>(`${URL}/users/${customerId}/features/${feature}`, { data: rest }),
  revokeCustomerFeature: ({ customerId, feature }: RevokeCustomerFeatureRequestData) =>
    del<CustomerFeatures>(`${URL}/users/${customerId}/features/${feature}`),
  searchCustomer: (email: string) => get<Customer[]>(`${BASE_URL}/api/admin/users?email=${email}`),
  searchCustomerFeedbacks: (email: string) =>
    get<CustomerVideoFeedback[]>(`${BASE_URL}/api/admin/feedback?email=${email}`),
  createCustomerDeduction: ({ customerId, ...rest }: CreateCustomerDeductionRequestData) =>
    post<Customer>(`${URL}/users/${customerId}/credits/deductions`, { data: rest }),
  createCustomerGrant: ({ customerId, ...rest }: CreateCustomerGrantRequestData) =>
    post<Customer>(`${URL}/users/${customerId}/credits/grants`, { data: rest }),
  // Per-customer plan feature overrides
  getCustomerFeatures: (customerId: string) => get<CustomerFeatures>(`${URL}/users/${customerId}/features`),
  // Get, search and update customers
  getCustomers: ({ endDate, filterBy, limit = 20, startDate }: GetCustomersRequestData) =>
    get<Customer[]>(
      `${URL}/users?date_gt=${startDate}&date_lt=${endDate}${
        filterBy === 'all' ? '' : `&subscription_type=${filterBy}`
      }&limit=${limit}`,
    ),
  // Get CSV data on donwload CSV click
  getCustomersCSV: ({ email, endDate, startDate }: GetCSVRequestData) =>
    get<Blob>(`${URL}/users/csv?${email ? `&email=${email}` : `date_gt=${startDate}&date_lt=${endDate}`}`),
  getCustomersFeedbacksCSV: ({ email, endDate, startDate }: GetCSVRequestData) =>
    get<Blob>(`${URL}/feedback/csv?${email ? `&email=${email}` : `date_gt=${startDate}&date_lt=${endDate}`}`),
  // Get all feedbacks
  getFeedback: ({ endDate, limit = 20, startDate }: GetCustomersFeedbackRequestData) =>
    get<CustomerVideoFeedback[]>(`${URL}/feedback?date_gt=${startDate}&date_lt=${endDate}&limit=${limit}`),
  getNewUserQuizSubmissions: ({ limit, offset }: GetNewUserQuizSubmissionsRequestData) =>
    get<PaginatedAdminNewUserQuizSubmissions>(`${URL}/new-user-quiz/submissions?offset=${offset}&limit=${limit}`),
  updateCustomer: ({ customerId, ...rest }: Partial<UpdateCustomerRequestData>) =>
    patch<Customer>(`${URL}/users/${customerId}`, { data: rest }),
  // Recording admin API
  ...RECORDING_ADMIN_API,
  // Visual Translation (on-screen text) jobs
  ...ONSCREEN_ADMIN_API,
};
